import React from 'react';
import ReactPopup from 'reactjs-popup';
import AnimatedNumber from 'animated-number-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import SpotlightCard from 'components/SpotlightCard/SpotlightCard';
import MultiCatchMouseTrapIcon from 'components/Icons/MultiCatchMouseTrapIcon';
import SmartBaitStationIcon from 'components/Icons/SmartRodentBaitModuleIcon';
import BaitIcon from 'components/Icons/Bait/VisitingBaitIcon';
import { ObjectItem } from 'api/clients/types';
import './ActivityCard.scss';
import SnapTrapMouseIcon from 'components/Icons/SnapTrapMouseIcon';
import SnapTrapRatIcon from 'components/Icons/SnapTrapRatIcon';

interface Props {
  object_data: ObjectItem;
}

const ActivityCard: React.FC<Props> = ({ object_data }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'ActivityCard' });
  const mctShow = object_data.mct_count > 0;
  const srbmShow = object_data.srbm_count > 0;
  const smstShow = object_data.smst_count > 0;
  const srstShow = object_data.srst_count > 0;
  return (
    // <div className="d-lg-flex flex-column d-none mt-2">
    <div className="flex flex-column mt-2 font-poppins">
      {mctShow && (
        <div className="card card-animate border rounded-4 w-100 m-auto mb-2" title={t('viewActivityDetails')}>
          <div className="card-body">
            <div className="activity-popup-scope">
              <ReactPopup
                trigger={
                  <div className="d-flex justify-content-between align-items-center">
                    <MultiCatchMouseTrapIcon height={30} width={30} />
                    <div className="d-flex flex-column align-items-end">
                      <h5 className="text-center">{t('captures')}</h5>
                      <span
                        className="d-flex justify-content-end align-items-start gap-1 text-danger mt-1"
                        style={{ width: '80px' }}
                      >
                        <AnimatedNumber
                          value={object_data.mct_activity.reduce(
                            (sum, mct_activity) => sum + mct_activity.tripped_caught_count,
                            0,
                          )}
                          formatValue={number =>
                            number.toLocaleString('en-US', { useGrouping: true }).replace(/,/g, ' ')
                          }
                          duration={1000}
                          className="animated-number fs-16 fw-bold"
                        />
                        <i className="ri-mickey-fill fs-14" />
                      </span>
                    </div>
                  </div>
                }
                position="top center"
                offsetY={15}
              >
                <SpotlightCard
                  className="custom-spotlight-card ml-grey-card border-0 rounded-3"
                  spotlightColor="rgba(130, 19, 221, 0.2)"
                >
                  <div className="text-dark">
                    {object_data.mct_activity.length === 0 ? (
                      <div className="text-success text-center mb-0">{t('noActivity')}</div>
                    ) : (
                      object_data.mct_activity.map(activity => (
                        <div key={activity.slug}>
                          <Link className="text-info" to={`/trap/mct/info/${activity.slug}`}>
                            {t('trapNo', { sequenceNumber: activity.sequence_number })}
                          </Link>{' '}
                          - {activity.tripped_caught_count} {t('mouse', { count: activity.tripped_caught_count })}
                        </div>
                      ))
                    )}
                  </div>
                </SpotlightCard>
              </ReactPopup>
            </div>
          </div>
        </div>
      )}
      {srbmShow && (
        <div className="card card-animate border rounded-4 w-100 m-auto mb-2" title={t('viewActivityDetails')}>
          <div className="card-body">
            <div className="activity-popup-scope">
              <ReactPopup
                trigger={
                  <div className="d-flex justify-content-between align-items-center">
                    <SmartBaitStationIcon height={30} width={30} />
                    <div className="d-flex flex-column align-items-end">
                      <h5 className="text-center">{t('consumption')}</h5>
                      <span
                        className="d-flex justify-content-end align-items-center gap-1 text-danger mt-1"
                        style={{ width: '80px' }}
                      >
                        <AnimatedNumber
                          value={object_data.srbm_activity.reduce(
                            (sum, srbm_activity) => sum + srbm_activity.eaten_bait,
                            0,
                          )}
                          formatValue={number =>
                            number.toLocaleString('en-US', { useGrouping: true }).replace(/,/g, ' ')
                          }
                          duration={1000}
                          className="animated-number fs-16 fw-bold"
                        />
                        <BaitIcon height={16} color={object_data.srbm_activity.length === 0 ? '#94C748' : '#f06548'} />
                      </span>
                    </div>
                  </div>
                }
                position="bottom center"
                offsetY={15}
                closeOnEscape
                className="rounded-3"
              >
                <SpotlightCard
                  className="custom-spotlight-card ml-grey-card border-0 rounded-3"
                  spotlightColor="rgba(130, 19, 221, 0.2)"
                >
                  <div className="text-dark">
                    {object_data.srbm_activity.length === 0 ? (
                      <p className="text-success text-center mb-0">{t('noActivity')}</p>
                    ) : (
                      object_data.srbm_activity.map(activity => (
                        <p key={activity.slug}>
                          <Link className="text-info" to={`/trap/srbm/info/${activity.slug}`}>
                            {t('baitStationNo', { sequenceNumber: activity.sequence_number })}
                          </Link>{' '}
                          - {activity.eaten_bait} {t('grams')}
                        </p>
                      ))
                    )}
                  </div>
                </SpotlightCard>
              </ReactPopup>
            </div>
          </div>
        </div>
      )}
      {smstShow && (
        <div className="card card-animate border rounded-4 w-100 m-auto mb-2" title={t('viewActivityDetails')}>
          <div className="card-body">
            <div className="activity-popup-scope">
              <ReactPopup
                trigger={
                  <div className="d-flex justify-content-between align-items-center">
                    <SnapTrapMouseIcon height={30} width={30} />
                    <div className="d-flex flex-column align-items-end">
                      <h5 className="text-center">{t('captures')}</h5>
                      <span
                        className="d-flex justify-content-end align-items-center gap-1 text-danger mt-1"
                        style={{ width: '80px' }}
                      >
                        <AnimatedNumber
                          value={object_data.smst_activity.reduce(
                            (sum, smst_activity) => sum + smst_activity.tripped_caught_count,
                            0,
                          )}
                          formatValue={number =>
                            number.toLocaleString('en-US', { useGrouping: true }).replace(/,/g, ' ')
                          }
                          duration={1000}
                          className="animated-number fs-16 fw-bold"
                        />
                        <i className="ri-mickey-fill fs-14" />
                      </span>
                    </div>
                  </div>
                }
                position="top center"
                offsetY={15}
              >
                <SpotlightCard
                  className="custom-spotlight-card ml-grey-card border-0 rounded-3"
                  spotlightColor="rgba(130, 19, 221, 0.2)"
                >
                  <div className="text-dark">
                    {object_data.smst_activity.length === 0 ? (
                      <div className="text-success text-center mb-0">{t('noActivity')}</div>
                    ) : (
                      object_data.smst_activity.map(activity => (
                        <div key={activity.slug}>
                          <Link className="text-info" to={`/trap/smst/info/${activity.slug}`}>
                            {t('trapNo', { sequenceNumber: activity.sequence_number })}
                          </Link>{' '}
                          - {activity.tripped_caught_count} {t('mouse', { count: activity.tripped_caught_count })}
                        </div>
                      ))
                    )}
                  </div>
                </SpotlightCard>
              </ReactPopup>
            </div>
          </div>
        </div>
      )}
      {srstShow && (
        <div className="card card-animate border rounded-4 w-100 m-auto mb-2" title={t('viewActivityDetails')}>
          <div className="card-body">
            <div className="activity-popup-scope">
              <ReactPopup
                trigger={
                  <div className="d-flex justify-content-between align-items-center ">
                    <SnapTrapRatIcon height={30} width={30} />
                    <div className="d-flex flex-column  align-items-end">
                      <h5 className="text-center">{t('captures')}</h5>
                      <span
                        className="d-flex justify-content-end align-items-center gap-1 text-danger mt-1"
                        style={{ width: '80px' }}
                      >
                        <AnimatedNumber
                          value={object_data.srst_activity.reduce(
                            (sum, srst_activity) => sum + srst_activity.tripped_caught_count,
                            0,
                          )}
                          formatValue={number =>
                            number.toLocaleString('en-US', { useGrouping: true }).replace(/,/g, ' ')
                          }
                          duration={1000}
                          className="animated-number fs-16 fw-bold"
                        />
                        <i className="ri-mickey-fill fs-14" />
                      </span>
                    </div>
                  </div>
                }
                position="top center"
                offsetY={15}
              >
                <SpotlightCard
                  className="custom-spotlight-card ml-grey-card border-0 rounded-3"
                  spotlightColor="rgba(130, 19, 221, 0.2)"
                >
                  <div className="text-dark">
                    {object_data.srst_activity.length === 0 ? (
                      <div className="text-success text-center mb-0">{t('noActivity')}</div>
                    ) : (
                      object_data.srst_activity.map(activity => (
                        <div key={activity.slug}>
                          <Link className="text-info" to={`/trap/srst/info/${activity.slug}`}>
                            {t('trapNo', { sequenceNumber: activity.sequence_number })}
                          </Link>{' '}
                          - {activity.tripped_caught_count} {t('rat', { count: activity.tripped_caught_count })}
                        </div>
                      ))
                    )}
                  </div>
                </SpotlightCard>
              </ReactPopup>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityCard;
